// BaseService - Axios instance with request/response interceptors for token management
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { appConfig } from '@/configs/app.config'
import { REQUEST_HEADER_AUTH_KEY, TOKEN_TYPE, API_TIMEOUT } from '@/constants/api.constant'
import { useAppStore } from '@/store/useAppStore'

class BaseService {
  private axiosInstance: AxiosInstance
  private isRedirecting = false
  private isHandlingPaymentRequired = false

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: appConfig.apiBaseUrl,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupRequestInterceptor()
    this.setupResponseInterceptor()
  }

  private setupRequestInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const accessToken = useAppStore.getState().accessToken

        if (accessToken && config.headers) {
          config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE} ${accessToken}`
        }

        return config
      },
      (error: AxiosError) => {
        return Promise.reject(error)
      }
    )
  }

  private setupResponseInterceptor(): void {
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const refreshToken = useAppStore.getState().refreshToken

            if (!refreshToken) {
              throw new Error('No refresh token available')
            }

            const response = await axios.post(`${appConfig.apiBaseUrl}/auth/refresh`, {
              refreshToken,
            })

            const { accessToken, refreshToken: newRefreshToken } = response.data.data

            useAppStore.getState().setCredentials(
              useAppStore.getState().user!,
              accessToken,
              newRefreshToken
            )

            if (originalRequest.headers) {
              originalRequest.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE} ${accessToken}`
            }

            return this.axiosInstance(originalRequest)
          } catch (refreshError) {
            // Only redirect once to prevent infinite loops
            if (!this.isRedirecting && typeof window !== 'undefined') {
              this.isRedirecting = true
              useAppStore.getState().signOutSuccess()
              
              // Use Next.js router instead of window.location to prevent hard reload
              setTimeout(() => {
                window.location.href = `${appConfig.unAuthenticatedEntryPath}?session=expired`
              }, 100)
            }

            return Promise.reject(refreshError)
          }
        }

        // Handle 402 Payment Required - unpaid term guard
        if (error.response?.status === 402) {
          const paymentResponse = error.response?.data as { data?: unknown } | undefined
          const paymentDetails = paymentResponse?.data || null
          useAppStore.getState().showTermUnpaidModal(paymentDetails)

          if (!this.isHandlingPaymentRequired && typeof window !== 'undefined') {
            const isDashboardPath = window.location.pathname.startsWith('/dashboard')
            if (!isDashboardPath) {
              this.isHandlingPaymentRequired = true
              window.location.href = '/auth/payment-required?reason=term-unpaid'
              setTimeout(() => {
                this.isHandlingPaymentRequired = false
              }, 1500)
            }
          }
          return Promise.reject(error)
        }

        // Handle 429 Too Many Requests - rate limiting
        if (error.response?.status === 429) {
          return Promise.reject(error)
        }

        return Promise.reject(error)
      }
    )
  }

  public getInstance(): AxiosInstance {
    return this.axiosInstance
  }
}

const baseServiceInstance = new BaseService()
export default baseServiceInstance.getInstance()
